import React, { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { isAxiosError } from 'axios';
import { useCart } from '../../context/CartContext';
import { apiService } from '../../config/api';
import type { CartContextValue, CartItem } from '../../types/cart';
import type { PrintifyOrder } from '../../types/printify';

interface ShippingInfo {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  country: string;
  region: string;
  address1: string;
  address2: string;
  city: string;
  zip: string;
}

const initialShippingInfo: ShippingInfo = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  country: '',
  region: '',
  address1: '',
  address2: '',
  city: '',
  zip: '',
};

const pageContainerClass = 'min-h-screen pt-24 md:pt-28 pb-12 bg-[var(--background)] transition-colors duration-300';
const surfaceClass = 'bg-[var(--surface)] text-[var(--text-primary)] border border-border rounded-islamic shadow-md';
const subtleTextClass = 'text-[var(--text-secondary)]';

const generateOrderId = (): string => {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return `order-${crypto.randomUUID()}`;
  }
  return `order-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
};

const PrintifyCheckout: React.FC = () => {
  const { cart, clearCart, getCartTotal } = (useCart() as CartContextValue) || {
    cart: [] as CartItem[],
    clearCart: () => undefined,
    getCartTotal: () => 0,
  };
  const navigate = useNavigate();

  const [shippingInfo, setShippingInfo] = useState<ShippingInfo>(initialShippingInfo);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const printifyItems = useMemo(
    () => cart.filter(item => Boolean(item.printifyProduct)),
    [cart]
  );

  const handleInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const { name, value } = event.target;
      setShippingInfo(prev => ({
        ...prev,
        [name]: value,
      }));
    },
    []
  );

  const validateForm = useCallback(() => {
    const requiredFields: Array<keyof ShippingInfo> = [
      'firstName',
      'lastName',
      'email',
      'country',
      'address1',
      'city',
      'zip',
    ];

    for (const field of requiredFields) {
      if (!shippingInfo[field]) {
        const label = field.replace(/([A-Z])/g, ' $1').toLowerCase();
        setError(`Please fill in ${label}`);
        return false;
      }
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(shippingInfo.email)) {
      setError('Please enter a valid email address.');
      return false;
    }

    return true;
  }, [shippingInfo]);

  const handleSubmit = useCallback(
    async (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      setError(null);

      if (!validateForm()) {
        return;
      }

      if (printifyItems.length === 0) {
        setError('No Printify items in cart.');
        return;
      }

      setLoading(true);

      try {
        const itemsByShop = printifyItems.reduce<Record<string, CartItem[]>>((acc, item) => {
          const shopId = item.printifyProduct?.shopId;
          if (!shopId) {
            return acc;
          }
          if (!acc[shopId]) {
            acc[shopId] = [];
          }
          acc[shopId].push(item);
          return acc;
        }, {});

        const orderPromises = Object.entries(itemsByShop).map(async ([shopId, items]) => {
          const orderPayload = {
            external_id: generateOrderId(),
            label: `TafsirArt Order - ${shippingInfo.firstName} ${shippingInfo.lastName}`,
            line_items: items.map(cartItem => ({
              product_id: cartItem.printifyProduct?.productId,
              variant_id: cartItem.printifyProduct?.variantId,
              quantity: cartItem.quantity,
            })),
            shipping_method: 1,
            send_shipping_notification: true,
            address_to: {
              first_name: shippingInfo.firstName,
              last_name: shippingInfo.lastName,
              email: shippingInfo.email,
              phone: shippingInfo.phone,
              country: shippingInfo.country,
              region: shippingInfo.region,
              address1: shippingInfo.address1,
              address2: shippingInfo.address2,
              city: shippingInfo.city,
              zip: shippingInfo.zip,
            },
          };

          return apiService.post<PrintifyOrder>(`/printify/shops/${shopId}/orders`, orderPayload);
        });

        const results = await Promise.all(orderPromises);

        clearCart();
        navigate('/checkout/success', {
          state: {
            orders: results,
            email: shippingInfo.email,
          },
        });
      } catch (err) {
        console.error('Error creating order:', err);
        if (isAxiosError(err)) {
          const message = (err.response?.data as { error?: string; message?: string } | undefined)?.error
            ?? err.response?.data?.message
            ?? err.message;
          setError(message ?? 'Failed to create order. Please try again.');
        } else {
          setError('Failed to create order. Please try again.');
        }
      } finally {
        setLoading(false);
      }
    },
    [clearCart, navigate, printifyItems, shippingInfo, validateForm]
  );

  if (printifyItems.length === 0) {
    return (
      <div className={pageContainerClass}>
        <div className="container-custom flex items-center justify-center min-h-[60vh]">
          <div className={`${surfaceClass} max-w-2xl w-full mx-auto text-center space-y-6 p-10`}>
            <h2 className="text-2xl font-bold text-[var(--text-primary)]">No Items to Checkout</h2>
            <p className={subtleTextClass}>Your cart is empty. Add some products to continue.</p>
            <button type="button" onClick={() => navigate('/store')} className="btn-primary px-6 py-3">
              Browse Store
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={pageContainerClass}>
      <div className="container-custom space-y-10 max-w-6xl mx-auto">
        <header className="text-center space-y-2">
          <h1 className="text-3xl font-bold text-[var(--text-primary)]">Checkout</h1>
          <p className={subtleTextClass}>Provide your shipping details to complete your Printify order.</p>
        </header>

        <div className="grid grid-cols-1 gap-8 lg:grid-cols-[minmax(0,1fr)_minmax(0,0.9fr)]">
          <section className={`${surfaceClass} p-6 space-y-6`}>
            <div>
              <h2 className="text-xl font-semibold text-[var(--text-primary)]">Shipping Information</h2>
              <p className={`${subtleTextClass} text-sm`}>All fields marked with * are required.</p>
            </div>

            {error && (
              <div className="rounded-lg border border-red-200/70 bg-red-50/70 dark:bg-red-900/20 dark:border-red-700/40 p-4">
                <p className="text-sm text-red-800 dark:text-red-200">{error}</p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <InputField label="First Name" name="firstName" value={shippingInfo.firstName} onChange={handleInputChange} required />
                <InputField label="Last Name" name="lastName" value={shippingInfo.lastName} onChange={handleInputChange} required />
              </div>
              <InputField type="email" label="Email" name="email" value={shippingInfo.email} onChange={handleInputChange} required />
              <InputField type="tel" label="Phone" name="phone" value={shippingInfo.phone} onChange={handleInputChange} />
              <InputField label="Country" name="country" value={shippingInfo.country} onChange={handleInputChange} placeholder="e.g., US, UK, CA" required />
              <InputField label="Region/State" name="region" value={shippingInfo.region} onChange={handleInputChange} />
              <InputField label="Address Line 1" name="address1" value={shippingInfo.address1} onChange={handleInputChange} required />
              <InputField label="Address Line 2" name="address2" value={shippingInfo.address2} onChange={handleInputChange} />
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <InputField label="City" name="city" value={shippingInfo.city} onChange={handleInputChange} required />
                <InputField label="ZIP/Postal Code" name="zip" value={shippingInfo.zip} onChange={handleInputChange} required />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full btn-primary py-4 text-lg disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Processing...' : 'Place Order'}
              </button>
            </form>
          </section>

          <aside className={`${surfaceClass} p-6 h-fit space-y-6`}>
            <div className="space-y-1">
              <h2 className="text-xl font-semibold text-[var(--text-primary)]">Order Summary</h2>
              <p className={`${subtleTextClass} text-sm`}>Review the items being fulfilled through Printify.</p>
            </div>

            <div className="space-y-4">
              {printifyItems.map(item => (
                <div key={item._id} className="flex gap-4 pb-4 border-b border-border">
                  {item.imageUrl && (
                    <img src={item.imageUrl} alt={item.title} className="w-20 h-20 object-cover rounded-md" />
                  )}
                  <div className="flex-1 space-y-1">
                    <h3 className="font-medium text-[var(--text-primary)]">{item.title}</h3>
                    {item.variant?.title && (
                      <p className={`${subtleTextClass} text-sm`}>{item.variant.title}</p>
                    )}
                    <p className={`${subtleTextClass} text-sm`}>Qty: {item.quantity}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold text-[var(--text-primary)]">
                      ${(item.price * item.quantity).toFixed(2)}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            <div className="space-y-2 border-t border-border pt-4">
              <div className="flex justify-between text-lg">
                <span className="font-semibold text-[var(--text-primary)]">Subtotal:</span>
                <span className="text-[var(--text-primary)]">${getCartTotal().toFixed(2)}</span>
              </div>
              <div className={`flex justify-between text-sm ${subtleTextClass}`}>
                <span>Shipping:</span>
                <span>Calculated at next step</span>
              </div>
              <div className="flex justify-between text-xl font-bold text-[var(--primary)] pt-2 border-t border-border">
                <span>Total:</span>
                <span>${getCartTotal().toFixed(2)}</span>
              </div>
            </div>

            <div className="rounded-lg border border-blue-200/60 dark:border-blue-700/40 bg-blue-50/70 dark:bg-blue-900/20 p-4">
              <p className="text-sm text-blue-800 dark:text-blue-100/80">
                <strong>Note:</strong> This order will be processed through Printify. You'll receive a confirmation email with
                tracking information once your order is fulfilled.
              </p>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
};

type InputFieldProps = {
  label: string;
  name: keyof ShippingInfo;
  value: string;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  type?: React.HTMLInputTypeAttribute;
  required?: boolean;
  placeholder?: string;
};

const InputField: React.FC<InputFieldProps> = ({ label, name, value, onChange, type = 'text', required, placeholder }) => (
  <div className="space-y-1">
    <label className="block text-sm font-medium text-[var(--text-primary)]">
      {label}
      {required ? ' *' : ''}
    </label>
    <input
      type={type}
      name={name}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      className="w-full px-4 py-2 border border-border rounded-lg bg-[var(--surface)] text-[var(--text-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--accent)] focus:border-transparent"
      required={required}
    />
  </div>
);

export default PrintifyCheckout;
